import React, { useState, useEffect, useCallback } from 'react';
import {
  createCompany,
  listCompanies,
  updateCompany,
  regenerateCompanyCode,
  setCompanyCompounds,
  deleteCompany,
  getProjectDropdownItems,
} from '../utils/api';
import './CompaniesPage.css';

/**
 * Companies — admin management of external companies.
 *
 * A company's staff self-register with a shared registration code and can then
 * maintain inventory ONLY for compounds assigned here. A company with no
 * assigned compounds can see and do nothing, which is the intended state for
 * one just created.
 *
 * The registration code is shown exactly once, when created or regenerated. It
 * is stored only as a hash, so there is no way to display it again — the UI has
 * to make that unmistakable at the moment it is on screen, because a dismissed
 * panel means regenerating.
 */
export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // create form
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [maxBrokers, setMaxBrokers] = useState('');
  const [creating, setCreating] = useState(false);

  // the show-once code panel
  const [issuedCode, setIssuedCode] = useState(null); // { code, companyName }
  const [copied, setCopied] = useState(false);

  // compound assignment
  const [projects, setProjects] = useState([]);
  const [assigningFor, setAssigningFor] = useState(null); // company object
  const [selectedIds, setSelectedIds] = useState([]);
  const [savingAssignment, setSavingAssignment] = useState(false);

  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setCompanies(await listCompanies());
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load companies.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const data = await createCompany({
        name,
        contact_email: contactEmail,
        max_brokers: maxBrokers ? parseInt(maxBrokers, 10) : null,
      });
      setIssuedCode({ code: data.registration_code, companyName: data.company.name });
      setCopied(false);
      setName(''); setContactEmail(''); setMaxBrokers('');
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not create the company.');
    } finally {
      setCreating(false);
    }
  };

  const handleRegenerate = async (company) => {
    // Destructive to anyone mid-signup with the old code, so confirm first.
    const ok = window.confirm(
      `Issue a new registration code for ${company.name}?\n\n` +
      'The current code stops working immediately. Staff who have already ' +
      'registered keep their accounts and are unaffected.'
    );
    if (!ok) return;
    setBusyId(company.id);
    try {
      const data = await regenerateCompanyCode(company.id);
      setIssuedCode({ code: data.registration_code, companyName: company.name });
      setCopied(false);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not regenerate the code.');
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (company) => {
    setBusyId(company.id);
    try {
      await updateCompany(company.id, { is_active: !company.is_active });
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not update the company.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (company) => {
    const ok = window.confirm(
      `Delete ${company.name}?\n\n` +
      `Its ${company.broker_count} staff account(s) are NOT deleted — they are ` +
      'detached from the company, which leaves them able to see nothing.'
    );
    if (!ok) return;
    setBusyId(company.id);
    try {
      await deleteCompany(company.id);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not delete the company.');
    } finally {
      setBusyId(null);
    }
  };

  const openAssign = async (company) => {
    setAssigningFor(company);
    setSelectedIds(company.compound_ids || []);
    if (projects.length === 0) {
      try {
        setProjects(await getProjectDropdownItems());
      } catch {
        setError('Could not load the compound list.');
      }
    }
  };

  const saveAssignment = async () => {
    setSavingAssignment(true);
    try {
      await setCompanyCompounds(assigningFor.id, selectedIds);
      setAssigningFor(null);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save the assignment.');
    } finally {
      setSavingAssignment(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(issuedCode.code);
      setCopied(true);
    } catch {
      setCopied(false); // clipboard blocked — the code is selectable on screen
    }
  };

  return (
    <div className="companies-page">
      {/* ── Show-once code ─────────────────────────────────────────────── */}
      {issuedCode && (
        <div className="companies-code-panel">
          <h3>Registration code for {issuedCode.companyName}</h3>
          <p className="companies-code-warn">
            Copy this now — it cannot be shown again. Anyone holding it can create
            a staff account under this company.
          </p>
          <div className="companies-code-row">
            <code className="companies-code">{issuedCode.code}</code>
            <button type="button" className="companies-btn primary" onClick={copyCode}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <button
            type="button"
            className="companies-btn subtle"
            onClick={() => setIssuedCode(null)}
          >
            {copied ? 'Done' : "I've copied it — dismiss"}
          </button>
        </div>
      )}

      {/* ── Create ─────────────────────────────────────────────────────── */}
      <div className="companies-card">
        <h2 className="companies-title">🏢 Add Company</h2>
        <p className="companies-desc">
          Creates a company and issues a registration code. Send the code to the
          company so their staff can register. They will see nothing until you
          assign compounds below.
        </p>
        <form onSubmit={handleCreate} className="companies-form">
          <label>
            Company name
            <input value={name} onChange={(e) => setName(e.target.value)}
                   required minLength={2} placeholder="Acme Developments" />
          </label>
          <label>
            Contact email
            <input type="email" value={contactEmail}
                   onChange={(e) => setContactEmail(e.target.value)}
                   required placeholder="ops@acme.example" />
            <small>For your records — staff sign in with their own emails.</small>
          </label>
          <label>
            Max staff accounts
            <input type="number" min={1} max={1000} value={maxBrokers}
                   onChange={(e) => setMaxBrokers(e.target.value)}
                   placeholder="Leave blank for no limit" />
          </label>
          <button type="submit" className="companies-btn primary" disabled={creating}>
            {creating ? 'Creating…' : 'Create company'}
          </button>
        </form>
      </div>

      {error && <div className="companies-error">{error}</div>}

      {/* ── List ───────────────────────────────────────────────────────── */}
      <div className="companies-card">
        <h2 className="companies-title">Companies</h2>
        {loading && <p className="companies-desc">Loading…</p>}
        {!loading && companies.length === 0 && (
          <p className="companies-desc">No companies yet.</p>
        )}
        {!loading && companies.length > 0 && (
          <table className="companies-table">
            <thead>
              <tr>
                <th>Company</th><th>Contact</th><th>Code</th>
                <th>Staff</th><th>Compounds</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className={c.is_active ? '' : 'inactive'}>
                  <td>{c.name}</td>
                  <td className="muted">{c.contact_email}</td>
                  <td className="muted">{c.code_last4 ? `…${c.code_last4}` : '—'}</td>
                  <td>
                    {c.broker_count}
                    {c.max_brokers ? ` / ${c.max_brokers}` : ''}
                  </td>
                  <td>
                    {c.compound_ids?.length
                      ? c.compound_ids.length
                      : <span className="warn">none — cannot do anything</span>}
                  </td>
                  <td>{c.is_active ? 'Active' : 'Deactivated'}</td>
                  <td className="actions">
                    <button type="button" onClick={() => openAssign(c)}
                            disabled={busyId === c.id}>Compounds</button>
                    <button type="button" onClick={() => handleRegenerate(c)}
                            disabled={busyId === c.id}>New code</button>
                    <button type="button" onClick={() => handleToggleActive(c)}
                            disabled={busyId === c.id}>
                      {c.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button type="button" className="danger"
                            onClick={() => handleDelete(c)}
                            disabled={busyId === c.id}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Assign compounds ───────────────────────────────────────────── */}
      {assigningFor && (
        <div className="companies-modal-backdrop" onClick={() => setAssigningFor(null)}>
          <div className="companies-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Compounds for {assigningFor.name}</h3>
            <p className="companies-desc">
              {assigningFor.name} can view and maintain only what is ticked here.
              Unticking removes access; it does not delete anything.
            </p>
            <div className="companies-checklist">
              {projects.length === 0 && <p className="companies-desc">Loading compounds…</p>}
              {projects.map((p) => {
                const id = p.id ?? p.project_id;
                return (
                  <label key={id} className="companies-check">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(id)}
                      onChange={(e) => setSelectedIds(
                        e.target.checked
                          ? [...selectedIds, id]
                          : selectedIds.filter((x) => x !== id)
                      )}
                    />
                    {p.name || p.project_name || `#${id}`}
                  </label>
                );
              })}
            </div>
            <div className="companies-modal-actions">
              <span className="muted">{selectedIds.length} selected</span>
              <button type="button" className="companies-btn subtle"
                      onClick={() => setAssigningFor(null)}>Cancel</button>
              <button type="button" className="companies-btn primary"
                      onClick={saveAssignment} disabled={savingAssignment}>
                {savingAssignment ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
